package horcrux.frontend.common.views.collections

import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.common.utils.invert
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.HasViewName
import horcrux.frontend.common.State
import horcrux.frontend.common.ViewRefreshState
import horcrux.frontend.common.analytics.ScreenViewEvent
import horcrux.frontend.common.views.collections.collectiondialogs.CollectionDialogs
import horcrux.frontend.common.views.components.containers.VStack
import horcrux.frontend.common.withDefaultCSS
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.Container
import mui.material.Drawer
import mui.material.DrawerAnchor
import mui.system.sx
import react.FC
import react.PropsWithChildren
import web.cssom.AlignItems
import web.cssom.pt
import web.cssom.px
import web.cssom.vh

external interface CollectionNavigatorProps : PropsWithChildren {
    var currentCollectionMetadataMaybeState: State<CollectionMetadata?>
    var documentMetadataList: List<DocumentMetadata>
    var collectionMetadataByIDState: State<CollectionMetadataByID>
    var viewRefreshState: ViewRefreshState
    var globalAppState: GlobalAppState
}

enum class CollectionNavigatorMenuOption(override val viewName: String) : HasViewName {
    COLLECTION_DETAILS("View Collection Details"),
    CREATE("Create a New Collection"),
    EDIT_NAME("Edit Collection Name"),
    IMPORT_DOCUMENTS("Import Documents to Your Collection"),
    DELETE("Delete Collection"),
    EDIT_CHAT_PAGES("Manage Your Public Chat Pages");
}

class CollectionNavigatorState {
    val showCreateCollectionDialogState = BooleanState()
    val showCollectionDetailsDialogState = BooleanState()
    val showEditNameDialogState = BooleanState()
    val showImportDocumentDialogState = BooleanState()
    val showDeleteCollectionDialogState = BooleanState()
    val importButtonLabelState = State(ImportButtonLabel.CREATE_COLLECTION_AND_IMPORT)
    val showManagePublicCollectionsState = BooleanState()
    val showChatPageDetailsDialogState = BooleanState()
    val showDeleteChatPageDialogState = BooleanState()
    private val collectionNavigatorDialogStates: List<BooleanState> = listOf(
        showCreateCollectionDialogState,
        showCollectionDetailsDialogState,
        showEditNameDialogState,
        showImportDocumentDialogState,
        showDeleteCollectionDialogState,
        showManagePublicCollectionsState,
        showChatPageDetailsDialogState,
        showDeleteChatPageDialogState
    )

    fun closeDialogs() {
        collectionNavigatorDialogStates.forEach { it.setFalse() }
    }

    fun showImportDocumentsDialog(importButtonLabel: ImportButtonLabel) {
        importButtonLabelState.set(importButtonLabel)
        showImportDocumentDialogState.setTrue()
    }
}

val CollectionNavigator = FC<CollectionNavigatorProps> { props ->
    val collectionNavigatorState = props.globalAppState.collectionNavigatorState
    val chatWithYourCollection = "Chat With Your Collection"
    props.globalAppState.createEffects()

    CollectionDialogs {
        this.collectionMetadataByIDState = props.collectionMetadataByIDState
        this.collectionNavigatorState = collectionNavigatorState
        this.currentCollectionMetadataMaybeState = props.currentCollectionMetadataMaybeState
        this.documentMetadataList = props.documentMetadataList
        this.globalAppState = props.globalAppState
        this.viewRefreshState = props.viewRefreshState
    }

    Drawer {
        this.title = "Collection Navigator"
        this.anchor = DrawerAnchor.top
        this.open = props.globalAppState.shouldShowCollectionNavigatorState.get()
        this.disableAutoFocus = true
        this.onClose = { _, _ -> props.globalAppState.toggleShowCollectionNavigatorState() }
        Container {
            this.sx {
                this.paddingTop = 8.px
                this.maxHeight = 75.vh
            }
            this.maxWidth = "md"
            this.withDefaultCSS()
            VStack {
                sx {
                    this.alignItems = AlignItems.center
                }

                CollectionSelectorAndMenu {
                    this.globalAppState = props.globalAppState
                    this.chatWithYourCollection = chatWithYourCollection
                }

                Button {
                    +chatWithYourCollection
                    this.variant = ButtonVariant.contained
                    this.sx {
                        this.margin = 10.pt
                    }
                    this.onClick = {
                        props.globalAppState.toggleShowCollectionNavigatorState()
                    }
                }

                // Only show the import button for non chat pages
                props.globalAppState.chatPageDataMaybeState.get().invert()?.let { _ ->
                    ImportButton {
                        this.currentCollectionIDMaybe = props.currentCollectionMetadataMaybeState.get()?.id
                        this.onClick = { importButtonLabel ->
                            when (importButtonLabel) {
                                ImportButtonLabel.IMPORT -> { _ ->
                                    props.currentCollectionMetadataMaybeState.get()?.let {
                                        props.globalAppState.analyticsClient.log(ScreenViewEvent.IMPORT_DOCUMENTS)
                                        collectionNavigatorState.showImportDocumentsDialog(importButtonLabel)
                                    }
                                }

                                ImportButtonLabel.CREATE_COLLECTION_AND_IMPORT -> { _ ->
                                    props.globalAppState.analyticsClient.log(ScreenViewEvent.CREATE_COLLECTION_AND_IMPORT)
                                    collectionNavigatorState.showImportDocumentsDialog(importButtonLabel)
                                }
                            }
                        }
                    }
                }
            }

            ManageDocumentCollection {
                this.globalAppState = props.globalAppState
            }
        }
    }
}
